import { useEffect, useState, useMemo } from "react";
import axios from 'axios';
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from 'react-fullscreen-loading';
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DataTable from 'react-data-table-component';
import ShowTime from "../../../utils/ShowTime/ShowTime";
import { FaEdit, FaRegIdCard, FaEye } from "react-icons/fa";
import countryList from 'react-select-country-list';
import Select from 'react-select'
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import { Button } from "react-bootstrap";
import { Tooltip } from 'react-tooltip'
import { min } from "date-fns";
import { useAuth, Can } from "../../../authentication/AuthContext";
import { useRoles } from "../../../utils/useRoles";
import TruncatedText, { TruncatedTextTooltip, DateCell, COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";

export default function AllUsers() {
  const navigate = useNavigate();
  // Server-verified userType (NOT localStorage, which is editable).
  const { userType, can } = useAuth();
  const isReseller = userType === 'reseller';

  // Admins and resellers see the same table, but never the same rows. The admin
  // endpoint is unscoped; the reseller one returns only that reseller's own
  // customers. They used to be a single endpoint with a row filter applied
  // inside an `if (userType === 'reseller')` branch — splitting them means the
  // scoping is enforced by which URL is called, not by a branch that could be
  // missed. Both return the same { users, meta.pagination } shape, so only the
  // base path differs here.
  const usersBase = isReseller
    ? `${process.env.REACT_APP_API_URL}/reseller/users`
    : `${process.env.REACT_APP_API_URL}/admin/users`;

  const [TableData, setTableData] = useState([]);
  const [screenloader, setScreenloader] = useState(true);
  const [TotalRecords, setTotalRecords] = useState();
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [pending, setPending] = useState(true);


  //filters
  const initialFilters = {
    'u.email': { operator: '', value: '' },
    'u.organization': { operator: '', value: '' },
    'u.country': { operator: '', value: '' },
    'u.created_at': { operator: '', value: '' },
    'ru.email': { operator: '', value: '' },
    // Filters on role_id rather than the joined role name: InnoDB has an index on
    // it (the fk_users_role foreign key implies one), and it stays correct if a
    // role is renamed.
    'u.role_id': { operator: '', value: '' },
  };
  const [filters, setFilters] = useState(initialFilters);
  const [createdAt, setCreatedAt] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [country, setCountry] = useState([]);
  const [resellerEmail, setResellerEmail] = useState("");
  const [roleFilter, setRoleFilter] = useState(null);
  const CountriesList = useMemo(() => countryList().getData(), []);

  // Role options for the filter come from the database, so a role created in the
  // Roles screen is immediately filterable with no change here.
  //
  // Reading them needs admin.roles.dropdown (or admin.roles.index) — see
  // useRoles. Sales gets the dropdown key by default, which turns this filter on
  // without also showing them the Roles tab. A role with neither gets no options
  // and the control is hidden rather than shown empty.
  const { roleOptions } = useRoles({ enabled: !isReseller });
  const roleFilterOptions = [{ value: '', label: 'All Roles' }, ...roleOptions];
  const showRoleFilter = !isReseller && roleOptions.length > 0;

  const handleFiltersChange = (key, value, operator) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: {
        operator: operator,
        value: value
      }
    }));
  };

  const handleFiltersReset = () => {
    setFilters(initialFilters);
    setCreatedAt("");
    setEmail("");
    setOrganization("");
    setCountry([]);
    setResellerEmail("");
    setRoleFilter(null);
  }


  const handleEnterKeyPress = (e) => {
    var value = e.target.value;
    if (value !== "") {
      value = `%${value}%`
    }
    if (e.key === 'Enter') {
      handleFiltersChange(e.target.id, value, 'LIKE')
    }
  };

  const handleCopyEmailList = () => {
    const emails = TableData.map(license => license.email);
    const emailList = (emails.join(', '));
    try {
      navigator.clipboard.writeText(emailList);
      toast.success(`${emails.length} Emails copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy email list to clipboard');
    }
  };

  const columns = [
    {
      name: "Actions",
      // Hidden entirely rather than left as an empty column: omit drops the
      // header as well as the cells.
      omit: !(can(['admin.users.update', 'admin.users.invoice']) || (isReseller && can('reseller.users.show'))),
      sortable: false,
      // Each icon is gated on the permission its destination actually needs, so a
      // role with read-only access sees the row without controls that would 403.
      cell: row => (
        <>
          <Can permission="admin.users.update">
            <FaEdit
              data-tooltip-id="user-index-edit"
              data-tooltip-content="edit user"
              data-tooltip-place="top"
              className="edit-icon"
              onClick={() => navigate(`/Admin/users/${row.id}/edit`)}
            />
          </Can>
          <Can permission="admin.users.invoice">
            <FaRegIdCard
              data-tooltip-id="user-index-view-invoice"
              data-tooltip-content="view invoice"
              data-tooltip-place="top"
              className="edit-icon"
              onClick={() => navigate(`/Admin/Licenses/Invoice/${row.id}`)}
            />
          </Can>
          {/* Portal check as well as permission: the Administrator role is
              is_super, so it holds every key including the reseller.* ones, and a
              bare <Can permission="reseller.users.show"> would show admins this
              icon on top of the two above. This is the reseller's alternative to
              edit + invoice, not an addition to them. */}
          {isReseller && (
            <Can permission="reseller.users.show">
              <FaEye
                data-tooltip-id="user-index-view"
                data-tooltip-content="view user"
                data-tooltip-place="top"
                className="edit-icon"
                onClick={() => navigate(`/Admin/Users/${row.id}`)}
              />
            </Can>
          )}
        </>
      )
    },
    {
      name: 'Reference ID',
      minWidth: `${COLUMN_WIDTHS.refId}px`,
      selector: row => (row.user_reference_id),
      sortField: "user_reference_id",
      sortable: true,
      cell: row => <TruncatedText text={row.user_reference_id} maxWidth={COLUMN_WIDTHS.refId} mono />,
    },
    ...(!isReseller ? [{
      name: 'Role',
      minWidth: '150px',
      // role_slug / role_name are joined in by getAllUser. Sorting stays on the
      // indexed `type` column (the portal) — sorting on the joined role name
      // would cost a filesort on every page of a large list, and grouping by
      // portal is the more useful ordering anyway.
      selector: row => row.role_name || row.type,
      sortField: "type",
      sortable: true,
      cell: row => {
        // Keyed by the four seeded slugs; any role created later falls through
        // to the neutral style rather than rendering unstyled.
        const colors = {
          admin: { bg: '#1e293b', color: '#fff' },
          sales: { bg: '#3b82f6', color: '#fff' },
          reseller: { bg: '#523ba8', color: '#fff' },
          user: { bg: '#e2e7f1', color: '#4b4b5a' },
        };
        const style = colors[row.role_slug] || { bg: '#e2e7f1', color: '#4b4b5a' };
        return (
          <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: style.bg,
            color: style.color,
          }}>
            {row.role_name || row.type || 'Customer'}
          </span>
        );
      },
    }] : []),
    ...(!isReseller ? [{
      name: 'Reseller',
      minWidth: `${COLUMN_WIDTHS.email}px`,
      selector: row => row.reseller_email,
      sortable: false,
      cell: row => row.reseller_email
        ? <TruncatedText text={row.reseller_email} maxWidth={COLUMN_WIDTHS.email} />
        : <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>,
    }] : []),
    {
      name: 'Email',
      minWidth: `${COLUMN_WIDTHS.email}px`,
      selector: row => row.email,
      sortField: "email",
      sortable: true,
      cell: row => <TruncatedText text={row.email} maxWidth={COLUMN_WIDTHS.email} />,
    },
    {
      name: 'Phone Number',
      minWidth: `${COLUMN_WIDTHS.phone}px`,
      selector: row => row.phone_number,
      sortField: "phone_number",
      sortable: true,
      cell: row => <TruncatedText text={row.phone_number} maxWidth={COLUMN_WIDTHS.phone} />,
    },
    {
      name: 'First Name',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => row.fname,
      sortField: "fname",
      sortable: true,
      cell: row => <TruncatedText text={row.fname} maxWidth={COLUMN_WIDTHS.name} />,
    },
    {
      name: 'Last Name',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => row.lname,
      sortField: "lname",
      sortable: true,
      cell: row => <TruncatedText text={row.lname} maxWidth={COLUMN_WIDTHS.name} />,
    },
    {
      name: 'Country',
      minWidth: `${COLUMN_WIDTHS.country}px`,
      selector: row => row.country,
      sortField: "country",
      sortable: true,
      cell: row => <TruncatedText text={row.country} maxWidth={COLUMN_WIDTHS.country} />,
    },
    {
      name: 'Organization',
      minWidth: `${COLUMN_WIDTHS.organization}px`,
      selector: row => row.organization,
      sortField: "organization",
      sortable: true,
      cell: row => <TruncatedText text={row.organization} maxWidth={COLUMN_WIDTHS.organization} />,
    },
    {
      name: 'Issued Licenses',
      minWidth: '150px',
      selector: row => row.license_count,
      sortField: "license_count",
      sortable: true,
      cell: row => <div>{row.license_count ? row.license_count : "-"}</div>,
    },
    {
      name: 'Last Login',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.login_at,
      sortField: "login_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.login_at)} />,
    },
    {
      name: 'Created At',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.created_at,
      sortField: "created_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.created_at)} />,
    },
    {
      name: 'Last Update',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.updated_at,
      sortField: "updated_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.updated_at)} />,
    },
    {
      name: 'Verified',
      selector: row => row.verified,
      sortField: "verified",
      sortable: true,
      cell: row => <div style={{ whiteSpace: 'pre-wrap' }}>{row.verified ? "yes" : "no"}</div>,
    },
  ];

  const getUsersList = async () => {
    // console.log(page,pageSize,sortBy,sortOrder,filters)
    try {
      const formattedFilters = Object.keys(filters)
        .filter(key => filters[key].value) // Filter out falsy values
        .map((key, index) => ({
          [`filters[${index}][column]`]: key,
          [`filters[${index}][operator]`]: filters[key].operator,
          [`filters[${index}][value]`]: filters[key].value,
        }))
        .reduce((acc, obj) => ({ ...acc, ...obj }), {});

      const response = await axios.get(usersBase, {
        params: {
          limit: pageSize,
          page: page,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...formattedFilters
        },
        headers: {
          'access-token': localStorage.getItem('accessToken'),
        },
      });
      setScreenloader(false);
      setPending(false);
      if (response.data.status) {
        const total = response.data.meta.pagination.total;
        setTotalRecords(total);
        setTableData(response.data.users);
      } else {
        toast.error('Something Went Wrong');
        console.log(response.data);
      }
    } catch (error) {
      setScreenloader(false);
      setPending(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error('Something Went Wrong');
      }
    }
  };

  useEffect(() => {
    setPending(true);
    getUsersList();
  }, [filters, page, pageSize, sortBy, sortOrder]);

  const now = new Date();

  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);

  const startOf3DaysAgo = new Date(now);
  startOf3DaysAgo.setDate(startOf3DaysAgo.getDate() - 2);
  startOf3DaysAgo.setHours(0, 0, 0, 0);

  const endOf3DaysAfter = new Date(now);
  endOf3DaysAfter.setDate(endOf3DaysAfter.getDate() + 3);
  endOf3DaysAfter.setHours(0, 0, 0, 0);

  const startOf1WeekAgo = new Date(now);
  startOf1WeekAgo.setDate(startOf1WeekAgo.getDate() - 7);
  startOf1WeekAgo.setHours(0, 0, 0, 0);

  const endOf1WeekAfter = new Date(now);
  endOf1WeekAfter.setDate(endOf1WeekAfter.getDate() + 7);
  endOf1WeekAfter.setHours(0, 0, 0, 0);

  const startOf1MonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  startOf1MonthAgo.setHours(0, 0, 0, 0);

  const endOf1MonthAfter = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  endOf1MonthAfter.setHours(0, 0, 0, 0);

  const startOf2MonthAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
  startOf2MonthAgo.setHours(0, 0, 0, 0);

  const endOf2MonthAfter = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());
  endOf2MonthAfter.setHours(0, 0, 0, 0);


  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className='content-page-bg'>
        <div className='content-page'>
          <div className='content'>
            <div className="row mb-3">
              <div className="col-md-6">
                <h2>Users</h2>
              </div>
              <Can permission="admin.users.create">
                <div className="col-md-6 text-end p-2">
                  <Button className="admin-btn" onClick={() => { navigate('/Admin/Users/Add') }}>+ Add User</Button>
                </div>
              </Can>
            </div>

            <div className="filters row">
              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    id="u.email"
                    placeholder='Email'
                    value={email}
                    onChange={e => { setEmail(e.target.value); }}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {email && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'u.email', value: email } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="u.organization"
                    placeholder='Organization'
                    value={organization}
                    onChange={e => { setOrganization(e.target.value); }}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {organization && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'u.organization', value: organization } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              {!isReseller && (
                <div className="col-md-3 mb-3">
                  <div className="input-group">
                    <input
                      type="email"
                      className="form-control"
                      id="ru.email"
                      placeholder='Reseller Email'
                      value={resellerEmail}
                      onChange={e => { setResellerEmail(e.target.value); }}
                      onKeyDown={handleEnterKeyPress}
                    />
                    {resellerEmail && (
                      <span
                        className="input-group-text"
                        role="button"
                        onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'ru.email', value: resellerEmail } })}
                      >
                        Apply
                      </span>
                    )}
                  </div>
                </div>
              )}


              {showRoleFilter && (
                <div className="col-md-3 mb-3">
                  <Select
                    placeholder="Role"
                    options={roleFilterOptions}
                    value={roleFilter}
                    onChange={(e) => {
                      setRoleFilter(e);
                      // 'All Roles' carries '' — getUsersList drops falsy filter
                      // values, so this clears the filter rather than matching ''.
                      handleFiltersChange('u.role_id', e.value, '=');
                    }}
                  />
                </div>
              )}

              <div className="col-md-3 mb-3">
                <Select placeholder="Country" options={CountriesList} value={country} onChange={(e) => { setCountry(e); handleFiltersChange('u.country', e.label, '=') }} />
              </div>
              <div className="col-md-3 mb-3">
                <Select
                  placeholder="Created At"
                  options={[
                    { value: "", label: "All" },
                    { value: startOfDay.toISOString(), label: "Today" },
                    { value: startOf3DaysAgo.toISOString(), label: "Past 3 Days" },
                    { value: startOf1WeekAgo.toISOString(), label: "Within a Week" },
                    { value: startOf1MonthAgo.toISOString(), label: "Witin a Month" },
                    { value: startOf2MonthAgo.toISOString(), label: "Within 2 Months" },
                  ]}
                  value={createdAt}
                  onChange={(e) => { setCreatedAt(e); handleFiltersChange('u.created_at', e.value, '>=') }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-6 col-md-6 mb-3 d-flex">
                <Button onClick={handleCopyEmailList} variant="primary" className="admin-btn">
                  Copy Email List
                </Button>
              </div>
              <div className="col-6 col-md-6 mb-3 d-flex justify-content-end align-items-end">
                <Button onClick={handleFiltersReset} variant="primary" className="admin-btn">
                  Clear all Filters
                </Button>
              </div>
            </div>
            <div className="row d-flex d-sm-none">
              <p>Total Records: <strong>{TotalRecords}</strong></p>
            </div>

            <div className="table-wrapper">
              <DataTable
                //data
                columns={columns}
                data={TableData}
                //style
                dense
                direction="auto"
                highlightOnHover
                noHeader
                persistTableHead
                pointerOnHover
                responsive
                subHeaderAlign="right"
                subHeaderWrap
                progressPending={pending}
                progressComponent={<CustomLoader />}
                // conditionalRowStyles={conditionalRowStyles}
                //pagination
                pagination
                paginationServer
                paginationPerPage={pageSize}
                paginationTotalRows={TotalRecords}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 150]}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage, currentPage) => { setPageSize(currentRowsPerPage); setPage(currentPage) }}
                sortServer
                onSort={(column, sortDirection) => { setSortBy(column.sortField); setSortOrder(sortDirection); }}
              />
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="user-index-view-invoice" style={{ zIndex: 99 }} />
      <Tooltip id="user-index-edit" style={{ zIndex: 99 }} />
      <Tooltip id="user-index-view" style={{ zIndex: 99 }} />
      <TruncatedTextTooltip />
    </div>
  );
}    